import { Box, useMediaQuery, useTheme, Slide, alpha } from '@mui/material';
import { useEffect, useRef } from 'react';
import { PanelHeader } from './PanelHeader';
import { APPBAR_HEIGHT } from '../layout/TopAppBar';
import { usePanelStore } from './createPanelStore';
import type { SidePanelProps } from './types';
import { DARK_SHELL_SURFACE } from '@billda/shared-ui';

/**
 * SidePanel - Reusable sliding panel component
 *
 * @example
 * ```tsx
 * const { isOpen, type, id, closePanel } = usePanelState();
 *
 * <SidePanel open={isOpen} onClose={closePanel} title="Details" initialWidth={600}>
 *   {type === 'user' && <UserDetails id={id} />}
 *   {type === 'site' && <SiteDetails id={id} />}
 * </SidePanel>
 * ```
 */
export function SidePanel({
  open,
  onClose,
  title,
  children,
  resizable = true,
  storageKey = 'panel',
  initialWidth,
  testId,
}: SidePanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { setWidth } = usePanelStore();
  const hasSetInitialWidth = useRef(false);

  // Set initial width when panel opens (only once)
  useEffect(() => {
    if (open && initialWidth && !hasSetInitialWidth.current) {
      setWidth(initialWidth);
      hasSetInitialWidth.current = true;
    }

    // Reset flag when panel closes
    if (!open) {
      hasSetInitialWidth.current = false;
    }
  }, [open, initialWidth, setWidth]);

  const cssVariable = `--${storageKey}-width`;

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box
        data-testid={testId}
        sx={{
          width: isMobile ? '100%' : `var(${cssVariable})`,
          height: isMobile ? '100vh' : `calc(100vh - ${APPBAR_HEIGHT}px)`,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme =>
            theme.palette.mode === 'dark'
              ? DARK_SHELL_SURFACE
              : 'background.default',
          borderLeft: isMobile
            ? 'none'
            : theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
              : `1px solid ${theme.palette.divider}`,
          borderTop: isMobile
            ? 'none'
            : theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
              : `1px solid ${theme.palette.divider}`,
          position: 'fixed',
          top: isMobile ? 0 : APPBAR_HEIGHT,
          right: 0,
          zIndex: isMobile ? 1200 : 100,
          overflow: 'hidden',
          willChange: resizable ? 'width' : undefined,
        }}
      >
        {title && <PanelHeader title={title} onClose={onClose} />}
        <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
      </Box>
    </Slide>
  );
}
